import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { PageShell } from "../components/PageShell";
import PageBackdrop from "../components/PageBackdrop";
import useJsonLd from "../hooks/useJsonLd";
import useSeo from "../hooks/useSeo";
import useContentHreflang from "../hooks/useContentHreflang";
import useContentRedirect from "../hooks/useContentRedirect";
import ContentLangSwitcher from "../components/ContentLangSwitcher";
import { API, pickContent, splitParagraphs, fileUrl } from "../lib/content";

const SITE = "https://lifeisaproject.global";

/**
 * EssayReaderV3 — reads a single published CMS item by slug under
 * /ideas/:slug or /research/:slug. Drafts return 404 from the API and
 * render the "not available" state. Research items may additionally
 * carry structured sections (executive summary, methodology, findings,
 * references), a PDF download and external links.
 *
 * CmsItemView is the pure renderer — it is also used by the
 * token-protected draft preview (/preview/:token), so a draft looks
 * exactly like the final published page.
 */

const RESEARCH_SECTIONS = [
    ["abstract",        { en: "Abstract",           es: "Resumen",               nl: "Samenvatting",              fr: "Résumé" }],
    ["executive_summary", { en: "Executive summary", es: "Resumen ejecutivo",    nl: "Managementsamenvatting",    fr: "Résumé exécutif" }],
    ["methodology",     { en: "Methodology",        es: "Metodología",           nl: "Methodologie",              fr: "Méthodologie" }],
    ["findings",        { en: "Main findings",      es: "Resultados principales", nl: "Belangrijkste bevindingen", fr: "Principales conclusions" }],
    ["references",      { en: "References",         es: "Referencias",           nl: "Referenties",               fr: "Références" }],
];

const PDF_LABEL = {
    en: "Read / download the paper (PDF)", es: "Leer / descargar el documento (PDF)",
    nl: "Lees / download het paper (PDF)", fr: "Lire / télécharger le document (PDF)",
};

export function CmsItemView({ item, mode = "ideas", previewToken = null }) {
    const { lang } = useLanguage();

    const { title, subtitle: dek, body } = pickContent(item, lang);
    const paragraphs = splitParagraphs(body);
    const isResearch = mode === "research";
    const meta = item
        ? [item.number, item.category, item.reading_time, (item.language || "en").toUpperCase()].filter(Boolean)
        : [];
    const researchSections = isResearch && item
        ? RESEARCH_SECTIONS.filter(([key]) => (item[key] || "").trim())
        : [];

    useSeo(item ? {
        title: item.seo_title || `${title} — Life is a Project`,
        description: item.seo_description || dek || undefined,
        path: `/${mode}/${item.slug}`,
        image: item.cover_image ? fileUrl(item.cover_image) : undefined,
    } : null);

    useContentHreflang(item, mode);

    // JSON-LD — Article schema for the item being read
    useJsonLd(item ? `article-${item.slug}` : null, item ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${SITE}/${mode}/${item.slug}`,
        headline: item.title,
        description: item.seo_description || item.subtitle || undefined,
        articleSection: item.category || (isResearch ? "Research" : "Ideas"),
        inLanguage: item.language || "en",
        mainEntityOfPage: `${SITE}/${mode}/${item.slug}`,
        url: `${SITE}/${mode}/${item.slug}`,
        isPartOf: { "@id": `${SITE}/#website` },
        publisher: { "@id": `${SITE}/#organization` },
        author: { "@type": "Organization", name: "Life is a Project", url: SITE },
    } : null);

    // JSON-LD — Breadcrumb path (Home → Ideas|Research → this item)
    useJsonLd(item ? `breadcrumb-${item.slug}` : null, item ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",  item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: isResearch ? "Research" : "Ideas", item: `${SITE}/${mode}` },
            { "@type": "ListItem", position: 3, name: item.title, item: `${SITE}/${mode}/${item.slug}` },
        ],
    } : null);

    const backTo = isResearch ? "/research" : "/ideas";
    const backLabel = isResearch
        ? (lang === "es" ? "← Investigación" : lang === "nl" ? "← Onderzoek" : lang === "fr" ? "← Recherche" : "← Research")
        : (lang === "es" ? "← Ideas" : lang === "nl" ? "← Ideeën" : lang === "fr" ? "← Idées" : "← Ideas");

    return (
        <PageShell testId="essay-reader" backdrop={<PageBackdrop variant={mode} soft />}>
            {!previewToken && (
                <Link to={backTo} data-testid="essay-back" className="link-editorial font-mono text-[11px] tracking-[0.28em] uppercase inline-block mb-10">
                    {backLabel}
                </Link>
            )}

            <header className="max-w-[820px] mb-14">
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span aria-hidden className="gold-thin" />
                    {meta.map((m, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span aria-hidden className="text-[color:var(--line-strong)]">·</span>}
                            <span>{m}</span>
                        </React.Fragment>
                    ))}
                </div>
                <ContentLangSwitcher item={item} basePath={mode} />
                <motion.h1
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light tracking-[-0.01em] leading-[1.05] text-[color:var(--navy)]"
                    style={{ fontSize: "clamp(36px, 5.4vw, 72px)" }}
                >
                    {title}
                </motion.h1>
                {dek && (
                    <p className="mt-8 font-display italic text-[22px] md:text-[26px] leading-[1.3] text-[color:var(--navy)]/80 max-w-[52ch]">
                        {dek}
                    </p>
                )}
                {(item.author || item.publication_date) && (
                    <p className="mt-6 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        {[item.author, item.publication_date].filter(Boolean).join(" · ")}
                    </p>
                )}
            </header>

            {item.cover_image && (
                <img
                    src={fileUrl(item.cover_image, previewToken)}
                    alt={title}
                    data-testid="content-cover-image"
                    className="w-full h-auto mb-14 border border-[color:var(--line)]"
                    loading="lazy"
                />
            )}

            {paragraphs.length > 0 && (
                <article className="max-w-[68ch] space-y-6">
                    {paragraphs.map((p, i) => (
                        <p key={i} className={`text-[17px] md:text-[19px] leading-[1.75] text-[color:var(--navy)]/90 ${i === 0 ? "first-letter:font-display first-letter:text-[64px] first-letter:leading-[0.9] first-letter:mr-2 first-letter:float-left first-letter:mt-1" : ""}`}>
                            {p}
                        </p>
                    ))}
                </article>
            )}

            {researchSections.length > 0 && (
                <div className="mt-16 border-t border-[color:var(--line-strong)]">
                    {researchSections.map(([key, labels], idx) => (
                        <section key={key} data-testid={`research-section-${key}`} className="grid grid-cols-12 gap-6 py-10 md:py-12 border-b border-[color:var(--line)]">
                            <div className="col-span-12 md:col-span-3">
                                <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                                    {String(idx + 1).padStart(2, "0")}
                                </div>
                                <h2 className="mt-3 font-display text-[24px] md:text-[28px] leading-[1.1] text-[color:var(--navy)]">
                                    {labels[lang] || labels.en}
                                </h2>
                            </div>
                            <div className="col-span-12 md:col-span-9 space-y-5 max-w-[62ch]">
                                {splitParagraphs(item[key]).map((p, i) => (
                                    <p key={i} className="text-[16px] md:text-[18px] leading-[1.72] text-[color:var(--navy)]/90">{p}</p>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {item.pdf_file && (
                <div className="mt-14">
                    <a
                        href={fileUrl(item.pdf_file, previewToken)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="research-pdf-download"
                        className="btn-editorial"
                    >
                        {PDF_LABEL[lang] || PDF_LABEL.en}
                        <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                    </a>
                </div>
            )}

            {(item.external_links || []).length > 0 && (
                <div className="mt-14 pt-8 border-t border-[color:var(--line)]">
                    <ul className="space-y-3">
                        {item.external_links.map((l, i) => (
                            <li key={i}>
                                <a href={l.url} target="_blank" rel="noopener noreferrer" className="link-editorial font-display italic text-[17px] text-[color:var(--navy)]/85">
                                    {l.label} ↗
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </PageShell>
    );
}

export default function EssayReaderV3({ mode = "ideas" }) {
    const { slug } = useParams();
    const { lang } = useLanguage();
    const [item, setItem] = useState(null);
    const [status, setStatus] = useState("loading");
    const navigate = useNavigate();
    useContentRedirect(status === "missing");

    useEffect(() => {
        setStatus("loading");
        setItem(null);
        axios
            .get(`${API}/content/${mode}/${slug}`)
            .then((r) => { setItem(r.data); setStatus("ready"); })
            .catch(() => setStatus("missing"));
    }, [mode, slug]);

    const backTo = mode === "research" ? "/research" : "/ideas";
    const backLabel = mode === "research"
        ? (lang === "es" ? "← Investigación" : lang === "nl" ? "← Onderzoek" : lang === "fr" ? "← Recherche" : "← Research")
        : (lang === "es" ? "← Ideas" : lang === "nl" ? "← Ideeën" : lang === "fr" ? "← Idées" : "← Ideas");

    if (status === "loading") {
        return (
            <PageShell testId="essay-reader">
                <div className="py-16 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">
                    {lang === "es" ? "Cargando…" : lang === "nl" ? "Laden…" : lang === "fr" ? "Chargement…" : "Loading…"}
                </div>
            </PageShell>
        );
    }

    if (status === "missing" || !item) {
        return (
            <PageShell testId="essay-reader">
                <div className="py-16">
                    <p className="font-display italic text-[24px] md:text-[32px] leading-[1.2] text-[color:var(--navy)] max-w-[36ch]">
                        {lang === "es" ? "Este texto no está disponible." : lang === "nl" ? "Dit stuk is niet beschikbaar." : lang === "fr" ? "Ce texte n’est pas disponible." : "This piece is not available."}
                    </p>
                    <button
                        onClick={() => navigate(backTo)}
                        className="mt-10 link-editorial font-mono text-[11px] tracking-[0.28em] uppercase"
                    >
                        {backLabel}
                    </button>
                </div>
            </PageShell>
        );
    }

    return <CmsItemView item={item} mode={mode} />;
}
